import { useEffect, useMemo, useRef, useState } from "react";
import RecurrenceCustomEditor from "@/components/RecurrenceCustomEditor";
import { POPOVER_ALIGN_CLASSES, type PopoverAlign } from "@/lib/popoverAlign";
import { PRESETS, isPresetRule, recurrenceShortLabel } from "@/lib/recurrence";

interface Props {
  value: string | null;
  onChange: (next: string | null) => void;
  iconOnly?: boolean;
  popoverAlign?: PopoverAlign;
}

export default function QuickRecurrencePicker({ value, onChange, iconOnly = false, popoverAlign = "start" }: Props) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<"presets" | "custom">("presets");
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) setMode("presets");
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function handle(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", handle);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", handle);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  function pick(next: string | null) {
    onChange(next);
    setOpen(false);
  }

  const isCustomActive = useMemo(() => Boolean(value && !isPresetRule(value)), [value]);
  const label = recurrenceShortLabel(value);
  const isSet = value !== null;

  return (
    <div className="relative inline-flex items-center" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`quick-meta-trigger ${isSet ? "quick-meta-trigger-set" : ""}`}
        aria-label={isSet ? `Повторение: ${label}` : "Указать повторение"}
        title={isSet ? `Повторение: ${label}` : "Указать повторение"}
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
        {label && !iconOnly && <span className="quick-meta-label">{label}</span>}
      </button>
      {open && mode === "presets" && (
        <div className={`details-popover top-full w-56 ${POPOVER_ALIGN_CLASSES[popoverAlign]}`} data-testid="quick-recurrence-popover">
          {PRESETS.map((p) => (
            <button
              key={p.rule}
              type="button"
              onClick={() => pick(p.rule)}
              className="details-popover-item"
            >
              <span className="flex-1 text-left">{p.label}</span>
              {p.rule === value && (
                <svg className="w-3 h-3 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              )}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setMode("custom")}
            className="details-popover-item"
          >
            <span className="flex-1 text-left">Настроить…</span>
            {isCustomActive && (
              <svg className="w-3 h-3 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            )}
          </button>
          {value && (
            <button
              type="button"
              onClick={() => pick(null)}
              className="details-popover-item text-red-600 dark:text-red-400"
            >
              Убрать
            </button>
          )}
        </div>
      )}
      {open && mode === "custom" && (
        <div className={`details-popover top-full w-72 ${POPOVER_ALIGN_CLASSES[popoverAlign]}`} data-testid="quick-recurrence-custom">
          <RecurrenceCustomEditor
            value={value}
            onSave={(rule) => pick(rule)}
            onBack={() => setMode("presets")}
            onCancel={() => setOpen(false)}
          />
        </div>
      )}
    </div>
  );
}
